import { faHistory } from '@fortawesome/pro-light-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { Box } from '@mui/material';
import { useQuery } from '@tanstack/react-query';
import { useMemo, useState } from 'react';

import { StyledAuditHistoryButton } from '~/common/components/AuditHistoryButton';
import BlockedPage from '~/common/components/BlockedPage';
import CrudGrid from '~/common/components/CrudGrid';
import type { GridDef } from '~/common/components/CrudGrid/GridDef';
import {
  ATTACHED_PROPERTY_TYPE_BY_NUMBER,
  PROPERTY_TYPES_BY_NUMBER,
} from '~/common/constants';
import { useAuditHistoryWindow } from '~/common/hooks/useAuditHistoryWindow';
import { useNavFeatureFlags } from '~/common/hooks/useNavFeatureFlags';
import useUrl from '~/common/hooks/useUrl';
import { typedApiClient } from '~/common/util/api-client';
import { formatCurrency } from '~/common/util/format';
import { AuditHistoryPageEnum } from '~/pages/AuditHistory/constants';

import ScrollIntoViewContainer from '../common/components/ScrollIntoViewContainer';
import { StyledLoan1003LayoutContainer } from '../common/components/StyledComponents';
import SubjectPropertySummariesForm from './Form/SubjectPropertySummariesForm';
import { SUBJECT_PROPERTY_OCCUPANCY_OPTIONS } from './Form/constants';

export default function SubjectProperty1003() {
  const includeSubjectPropertyFlag =
    useNavFeatureFlags().IncludeSubjectProperty;

  if (!includeSubjectPropertyFlag) {
    return <BlockedPage />;
  }

  return <SubjectProperty1003Layout />;
}

function SubjectProperty1003Layout() {
  const { obfuscatedLoanIdentifier } = useUrl();

  const { data: subjectPropertyData, isLoading } = useQuery({
    queryKey: ['subjectPropertySummary', obfuscatedLoanIdentifier],
    queryFn: async () => {
      const { data } =
        await typedApiClient.v1.subjectPropertyGetSubjectPropertySummary(
          obfuscatedLoanIdentifier,
        );
      return data;
    },
  });

  const [showForm, setShowForm] = useState(true);
  const { handleChangePageId } = useAuditHistoryWindow('sectioned');

  const handleEditMode = () => {
    setShowForm((prev) => !prev);
  };

  const handleClose = () => {
    setShowForm(false);
  };

  const subjectPropertyRow = useMemo(() => {
    if (!subjectPropertyData) return [];

    const { addressLine1, unit, city, state, zipCode } =
      subjectPropertyData.propertyAddress ?? {};
    const propertyAddress = [addressLine1, unit, city, state, zipCode]
      .filter(Boolean)
      .join(', ');

    return [
      {
        propertyAddress,
        addressLine1: addressLine1 ?? '',
        unit: unit ?? '',
        city: city ?? '',
        state: state ?? '',
        zipCode: zipCode ?? '',
        mortgagePayment:
          subjectPropertyData.proposedPrincipalAndInterestPayment ?? null,
        netRent: subjectPropertyData.totalNetRentalIncome ?? null,
        occupancyType:
          SUBJECT_PROPERTY_OCCUPANCY_OPTIONS.find(
            (opt) => opt.value === subjectPropertyData.occupancyType,
          )?.label ?? '',
        propertyType:
          PROPERTY_TYPES_BY_NUMBER[
            subjectPropertyData.propertyType as keyof typeof PROPERTY_TYPES_BY_NUMBER
          ] ?? '',
        attachmentType:
          ATTACHED_PROPERTY_TYPE_BY_NUMBER[
            subjectPropertyData.attachedPropertyType
          ] ?? '',
        appraisedValue: subjectPropertyData.appraisedValue ?? null,
        currentAddressId: subjectPropertyData.currentAddressId ?? null,
        mixedUseProperty: subjectPropertyData.mixedUseProperty ?? false,
        ruralProperty: subjectPropertyData.ruralProperty ?? false,
      },
    ];
  }, [subjectPropertyData]);

  const columns = [
    {
      fieldName: 'propertyAddress',
      headerName: 'Property Address',
    },
    {
      fieldName: 'occupancyType',
      headerName: 'Occupancy',
    },
    {
      fieldName: 'propertyType',
      headerName: 'Property Type',
    },
    {
      fieldName: 'attachmentType',
      headerName: 'Property Attachment Type',
    },
    {
      fieldName: 'appraisedValue',
      headerName: 'Appraised Value',
      isNumeric: true,

      renderCell: (val: number | null) =>
        val === null ? '' : formatCurrency(val),
    },
    {
      fieldName: 'mortgagePayment',
      headerName: 'Mortgage Payment',
      isNumeric: true,

      renderCell: (val: number | null) =>
        val === null ? '' : formatCurrency(val),
    },
    {
      fieldName: 'netRent',
      headerName: 'Net Rent',
      isNumeric: true,

      renderCell: (val: number | null) =>
        val === null ? '' : formatCurrency(val),
    },
  ];

  const gridDef: GridDef = {
    uniqueIdAttributeFields: ['propertyAddress'],
    allowFilters: false,
    gridLanguage: {
      singularItem: 'Subject Property',
      pluralItem: 'Subject Properties',
    },
    getDialogDeleteMessage: () => '',
  };

  return (
    <StyledLoan1003LayoutContainer>
      <Box
        sx={{
          justifyContent: 'flex-end',
          display: 'flex',
          width: '100%',
          marginBottom: 6,
        }}
      >
        <StyledAuditHistoryButton
          rightIcon={<FontAwesomeIcon icon={faHistory} />}
          marginLeft={'0.75rem'}
          data-testid="audit-history--button"
          aria-label={'Audit History'}
          isPrimary={false}
          onClick={() => {
            handleChangePageId(AuditHistoryPageEnum.SUBJECT_PROPERTY_1003);
          }}
        />
      </Box>
      <CrudGrid
        gridDef={gridDef}
        columns={columns}
        data={subjectPropertyRow}
        readOnly
        selectedRow={null}
        isLoading={isLoading}
        onRowEditClick={handleEditMode}
        onDelete={() => {}}
      />

      {showForm && subjectPropertyData && (
        <ScrollIntoViewContainer sx={{ marginTop: '1.5rem', width: '100%' }}>
          <SubjectPropertySummariesForm
            rowToEdit={subjectPropertyData}
            onClose={handleClose}
          />
        </ScrollIntoViewContainer>
      )}
    </StyledLoan1003LayoutContainer>
  );
}
